import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FaPhoneAlt, FaMapMarkerAlt, FaEnvelope } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Footer.css";
import { settingsAPI, socialsAPI } from "../../api/apiSystem";
import Logo from "../../assets/images/logoW.png";
import Rechnung from "../../assets/images/Rechnung.png";
import visa from "../../assets/images/visa.png";
import mastercard from "../../assets/images/mastercard.png";
import paypal from "../../assets/images/paypal.png";
import klarna from "../../assets/images/klarna.jpg";

export default function Footer() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [socials, setSocials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const [settingsRes, socialsRes] = await Promise.all([
          settingsAPI.index(),
          socialsAPI.index(),
        ]);

        if (settingsRes?.success && settingsRes.data?.data?.length > 0) {
          setSettings(settingsRes.data.data[0]);
        }

        if (socialsRes?.success && socialsRes.data?.data?.length > 0) {
          setSocials(socialsRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching settings or socials:", err);
        setError("Failed to load settings or social links.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // console.log("🚀 ~ Footer ~ settings:", settings.footer_phone);
  return (
    <footer className="footer text-white pt-5">
      <Container fluid className="px-5">
        {/* Slogan */}
        <Row className="justify-content-center text-center mb-4">
          <Col xs={12}>
            <p className="footer-slogan fw-semibold fs-5">
              {t("Footer.slogan")}
            </p>
          </Col>
        </Row>

        <Row className="gy-4 text-start">
          {/* Contact */}
          <Col xs={12} md={6} lg={3}>
            {settings?.footer_logo && (
              <img
                src={settings.footer_logo}
                // src={Logo}
                alt="Transcirox Logo"
                className="mb-3"
                style={{ maxWidth: "150px" }}
              />
            )}
            <div className="d-flex flex-column gap-1">
              <h5>
                {settings?.footer_col1_title || t("Footer.contact.title")}
              </h5>
              {settings?.footer_phone && (
                <div className="text-white d-flex align-items-start gap-2">
                  <FaPhoneAlt />
                  <div
                    dangerouslySetInnerHTML={{ __html: settings?.footer_phone }}
                  />
                </div>
              )}
            </div>
            {settings?.footer_address && (
              <p>
                <FaMapMarkerAlt className="me-2" />
                <span
                  dangerouslySetInnerHTML={{ __html: settings?.footer_address }}
                />
              </p>
            )}
            {settings?.footer_email && (
              <p>
                <FaEnvelope className="me-2" /> {settings.footer_email}
              </p>
            )}
          </Col>

          {/* Legal Information */}
          <Col xs={12} md={6} lg={3}>
            <h5>{t("Footer.legalInformation.title")}</h5>
            <ul className="list-unstyled footer-list">
              <li>
                <Link to="/copyright" className="footer-link">
                  ❯ {t("Footer.legalInformation.copyrightLaw")}
                </Link>
              </li>
              <li>
                <Link to="/legal-notice" className="footer-link">
                  ❯ {t("Footer.legalInformation.legalNotice")}
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="footer-link">
                  ❯ {t("Footer.legalInformation.cookiePolicy")}
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="footer-link">
                  ❯ {t("Footer.legalInformation.privacyPolicy")}
                </Link>
              </li>
              <li>
                <Link to="/user-terms" className="footer-link">
                  ❯ {t("driverTerms.agb")}
                </Link>
              </li>
            </ul>
          </Col>

          {/* Information */}
          <Col xs={12} md={6} lg={3}>
            <h5>
              {settings?.footer_col4_title || t("Footer.information.title")}
            </h5>
            <ul className="list-unstyled footer-list">
              <li>
                <Link to="/information" className="footer-link">
                  ❯ {t("Footer.information.weAreEverywhere")}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="footer-link">
                  ❯ {t("Footer.information.contact")}
                </Link>
              </li>
              <li>
                <Link to="/information" className="footer-link">
                  ❯ {t("Footer.information.courierServices")}
                </Link>
              </li>
            </ul>
          </Col>

          {/* Payment Methods */}
          <Col xs={12} md={6} lg={3}>
            <h5>
              {settings?.footer_col3_title || t("Footer.paymentMethods.title")}
            </h5>
            <div className="mt-3">
              <Row className="g-2">
                <Col xs={6}>
                  <img src={Rechnung} alt="Rechnungen" className="img-fluid" />
                </Col>
                <Col xs={6}>
                  <img
                    src={mastercard}
                    alt="Mastercard"
                    className="img-fluid"
                  />
                </Col>
                <Col xs={6}>
                  <img src={paypal} alt="Paypal" className="img-fluid" />
                </Col>
                <Col xs={6}>
                  <img src={visa} alt="Visa" className="img-fluid" />
                </Col>
                <Col xs={6}>
                  <img src={klarna} alt="Klarna" className="img-fluid" />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Social Media */}
      <div className="text-center footer-social mt-5 py-4">
        <p className="text-warning fw-bold">
          {t("Footer.socialMedia.followUs")}
        </p>
        <div className="d-flex justify-content-center gap-3 fs-4">
          {socials
            .filter((s) => s.social_url)
            .map((social) => (
              <a
                key={social.id}
                href={social.social_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white"
              >
                <i className={social.social_icon}></i>
              </a>
            ))}
        </div>
      </div>
    </footer>
  );
}
